// cspell:disable
/**
 * Carrier/service enums and correlated schemas, extracted from
 * carriers-and-services so schema-only consumers (POS bundle) don't carry the
 * label maps, data tables, and helpers. That module re-exports everything
 * here, so existing importers are unaffected.
 */
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export enum StandardCarrier {
  FEDEX = "FedEx",
  UPS = "UPS®",
  USPS = "USPS",
  DHLExpress = "DHLExpress",
  VEHO = "Veho",
  APC = "APC",
  AMAZON = "Amazon",
  DHL = "DHL eCommerce",
  GLS = "GLS",
  ASENDIA = "ASENDIA",
  SPEE_DEE = "SpeeDee",
  PASSPORT_GLOBAL = "PassportGlobal",
  LANDMARK_GLOBAL = "Landmark Global",
  ONTRAC = "OnTrac",
  UPS_MAIL_INNOVATIONS = "UPS Mail Innovations",
}

export enum Carriers {
  FEDEX = "FedEx",
  UPS = "UPS",
  UPS_SUREPOST = "UPSSurePost",
  USPS = "USPS",
  USPS_RETURNS = "USPSReturns",
  USPS_SHIP = "UspsShip",
  FEDEX_SMART_POST = "FedExSmartPost",
  AMAZON = "AmazonShipping",
  DHL_EXPRESS = "DHLExpress",
  DHL_ECS = "DhlEcs",
  VEHO = "Veho",
  APC = "APC",
  FIRST_MILE = "FirstMile",
  AUSTRALIA_POST = "AustraliaPost",
  CANADA_POST = "CanadaPost",
  GLS = "GSO",
  ASENDIA = "AsendiaUsa",
  ROYAL_MAIL_V3 = "RoyalMailV3",
  SPEE_DEE = "SpeeDee",
  PASSPORT_GLOBAL = "PassportGlobal",
  EPOST_GLOBAL = "ePostGlobal",
  LANDMARK_GLOBAL = "LandmarkGlobal", // Not a supported EasyPost carrier. Direct integration
  ETA_EXPRESS = "ETA Express", // Not a supported EasyPost carrier. Direct integration
  ONTRAC = "OnTracV3",
  GLOBALE = "GlobalE", // Not a supported EasyPost carrier. Direct integration
  UPS_MAIL_INNOVATIONS = "UPSMailInnovations",
  PUROLATOR = "Purolator",
  UNIUNI = "UniUni",
  STALLION_EXPRESS = "StallionExpress", // Not a supported EasyPost carrier. Direct integration
  CHITCHATS = "ChitChats", // Not a supported EasyPost carrier. Direct integration

  POST_NL = "PostNL", // Not a supported EasyPost carrier. Via Stallion Express
  INTELCOM = "Intelcom", // Not a supported EasyPost carrier. Via Stallion Express
  FLEET_OPTICS = "FleetOptics", // Not a supported EasyPost carrier. Via Stallion Express
  ICS_COURIER = "ICS", // Not a supported EasyPost carrier. Via Stallion Express
  WHIZ_DELIVERY = "WhizDelivery", // Not a supported EasyPost carrier. Via Stallion Express
  // https://seller-us.tiktok.com/university/essay?knowledge_id=1830506744514347&lang=en
  TIKTOK_SHOP = "Tik Tok",
  CANPAR_EXPRESS = "CanparExpress",
}

export enum FedexServices {
  FEDEX_INTERNATIONAL_PRIORITY_EXPRESS = "FEDEX_INTERNATIONAL_PRIORITY_EXPRESS",
  FEDEX_INTERNATIONAL_CONNECT_PLUS = "FEDEX_INTERNATIONAL_CONNECT_PLUS",
  INTERNATIONAL_FIRST = "INTERNATIONAL_FIRST",
  FEDEX_GROUND = "FEDEX_GROUND",
  FEDEX_GROUND_HOME_DELIVERY = "GROUND_HOME_DELIVERY",
  FEDEX_2_DAY = "FEDEX_2_DAY",
  PRIORITY_OVERNIGHT = "PRIORITY_OVERNIGHT",
  FEDEX_FIRST_FREIGHT = "FEDEX_FIRST_FREIGHT",
  FEDEX_EXPRESS_SAVER = "FEDEX_EXPRESS_SAVER",
  FEDEX_3_DAY_FREIGHT = "FEDEX_3_DAY_FREIGHT",
  FEDEX_2_DAY_AM = "FEDEX_2_DAY_AM",
  FEDEX_2_DAY_FREIGHT = "FEDEX_2_DAY_FREIGHT",
  INTERNATIONAL_ECONOMY = "INTERNATIONAL_ECONOMY",
  FIRST_OVERNIGHT = "FIRST_OVERNIGHT",
  STANDARD_OVERNIGHT = "STANDARD_OVERNIGHT",
  SMART_POST = "SMART_POST",
  FEDEX_1_DAY_FREIGHT = "FEDEX_1_DAY_FREIGHT",
  INTERNATIONAL_PRIORITY = "INTERNATIONAL_PRIORITY",
  FEDEX_INTERNATIONAL_PRIORITY = "FEDEX_INTERNATIONAL_PRIORITY",
  EUROPE_FIRST_INTERNATIONAL_PRIORITY = "EUROPE_FIRST_INTERNATIONAL_PRIORITY",
  INTERNATIONAL_ECONOMY_FREIGHT = "INTERNATIONAL_ECONOMY_FREIGHT",
  INTERNATIONAL_PRIORITY_FREIGHT = "INTERNATIONAL_PRIORITY_FREIGHT",
}

export enum UspsServices {
  GroundReturn = "GroundReturn",
  GroundAdvantageReturn = "GroundAdvantageReturn",
  ExpressMailInternational = "ExpressMailInternational",
  PriorityMailInternational = "PriorityMailInternational",
  FirstClassPackageInternationalService = "FirstClassPackageInternationalService",
  GroundAdvantage = "GroundAdvantage",
  PriorityMailReturn = "PriorityMailReturn",
  Priority = "Priority",
  Express = "Express",
  FirstClass = "First",
  FirstClassPkgReturn = "FirstClassPkgReturn",
  PriorityMailExpressReturn = "PriorityMailExpressReturn",
  MediaMail = "MediaMail",
  LibraryMail = "LibraryMail",
  FirstClassMailInternational = "FirstClassMailInternational",
}

export enum UpsServices {
  Ground = "Ground",
  NextDayAir = "NextDayAir",
  ThreeDaySelect = "3DaySelect",
  SecondDayAirAM = "2ndDayAirAM",
  NextDayAirEarlyAM = "NextDayAirEarlyAM",
  WorldWideSaver = "UPSSaver",
  WorldWideExpressPlus = "ExpressPlus",
  WorldWideExpress = "Express",
  WorldWideExpedited = "Expedited",
  NextDayAirSaver = "NextDayAirSaver",
  UPSStandard = "UPSStandard",
  SecondDayAir = "2ndDayAir",
  SurePostUnder1Lb = "SurePostUnder1Lb",
  SurePostOver1Lb = "SurePostOver1Lb",
  ExpressEarly = "ExpressEarly",
  ExpressSaver = "ExpressSaver",
}

export enum AmazonServices {
  Ground = "Amazon Shipping Ground",
}

export enum DhlExpressServices {
  BreakBulkEconomy = "BreakBulkEconomy",
  BreakBulkExpress = "BreakBulkExpress",
  DomesticEconomySelect = "DomesticEconomySelect",
  DomesticExpress = "DomesticExpress",
  DomesticExpress1030 = "DomesticExpress1030",
  DomesticExpress1200 = "DomesticExpress1200",
  EconomySelect = "EconomySelect",
  EconomySelectNonDoc = "EconomySelectNonDoc",
  EuroPack = "EuroPack",
  EuropackNonDoc = "EuropackNonDoc",
  Express1030 = "Express1030",
  Express1030NonDoc = "Express1030NonDoc",
  Express1200NonDoc = "Express1200NonDoc",
  Express1200 = "Express1200",
  Express900 = "Express900",
  Express900NonDoc = "Express900NonDoc",
  ExpressEasy = "ExpressEasy",
  ExpressEasyNonDoc = "ExpressEasyNonDoc",
  ExpressEnvelope = "ExpressEnvelope",
  ExpressWorldwide = "ExpressWorldwide",
  ExpressWorldwideB2C = "ExpressWorldwideB2C",
  ExpressWorldwideB2CNonDoc = "ExpressWorldwideB2CNonDoc",
  ExpressWorldwideECX = "ExpressWorldwideECX",
  ExpressWorldwideNonDoc = "ExpressWorldwideNonDoc",
  FreightWorldwide = "FreightWorldwide",
  GlobalmailBusiness = "GlobalmailBusiness",
  JetLine = "JetLine",
  JumboBox = "JumboBox",
  LogisticsServices = "LogisticsServices",
  SameDay = "SameDay",
  SecureLine = "SecureLine",
  SprintLine = "SprintLine",
  MedicalExpressNonDoc = "MedicalExpressNonDoc",
}

export enum DhlEcsServices {
  DhlParcelExpedited = "DHLParcelExpedited",
  DhlParcelExpeditedMax = "DHLParcelExpeditedMax",
  DhlParcelGround = "DHLParcelGround",
  DhlParcelInternationalDirect = "DHLParcelInternationalDirect",
  DhlParcelInternationalStandard = "DHLParcelInternationalStandard",
  DhlPacketInternational = "DHLPacketInternational",
  DhlParcelInternationalDirectPriority = "DHLParcelInternationalDirectPriority",
  DhlParcelInternationalDirectStandard = "DHLParcelInternationalDirectStandard",
  DhlParcelInternationalDirectSMB = "DHLParcelInternationalDirectSMB",
  DhlParcelInternationalStandardSMB = "DHLParcelInternationalStandardSMB",
  DhlBPMExpedited = "DHLBPMExpedited",
  DhlBPMGround = "DHLBPMGround",
  DhlSmartMailParcelReturnGround = "DHLSmartMailParcelReturnGround",
  DhlSmartMailParcelReturnPlus = "DHLSmartMailParcelReturnPlus",
  DhlSmartMailParcelReturnLight = "DHLSmartMailParcelReturnLight",
}

export enum VehoServices {
  NextDay = "nextDay",
  SameDay = "sameDay",
}

export enum ApcServices {
  ParcelConnectBookService = "parcelConnectBookService",
  ParcelConnectExpeditedDDP = "parcelConnectExpeditedDDP",
  ParcelConnectExpeditedDDU = "parcelConnectExpeditedDDU",
  ParcelConnectPriorityDDP = "parcelConnectPriorityDDP",
  ParcelConnectPriorityDDPDelcon = "parcelConnectPriorityDDPDelcon",
  ParcelConnectPriorityDDU = "parcelConnectPriorityDDU",
  ParcelConnectPriorityDDUDelcon = "parcelConnectPriorityDDUDelcon",
  ParcelConnectPriorityDDUPQW = "parcelConnectPriorityDDUPQW",
  ParcelConnectStandardDDU = "parcelConnectStandardDDU",
  ParcelConnectStandardDDUPQW = "parcelConnectStandardDDUPQW",
  ParcelConnectPacketDDU = "parcelConnectPacketDDU",
  // New services added by EasyPost
  ApcEPMIDDP = "APCePMIDDP",
  ApcEPMEIDDP = "APCePMEIDDP",
  ApcPacketDDP = "APCPacketDDP",
  ApcUSPSInternationalPriorityMailExpress = "APCUSPSInternationalPriorityMailExpress",
  ApcDDUPlus = "APCDDU+",
  ApcPriorityDDUPlus = "APCPriorityDDU+",
  ApcPriorityDDUPlusDelcon = "APCPriorityDDU+Delcon",
  ApcEPacketDDUPlus = "APCePacketDDU+",
  ApcPMIDDUPlus = "APCPMIDDU+",
  ApcPriorityPQWPlus = "APCPriorityPQW+",
  ApcUSA = "APCUSA",
  ApcUSAGround = "APCUSAGround",
  ApcUSAExpress = "APCUSAExpress",
  ApcUSA2 = "APCUSA2",
  ApcUSA3 = "APCUSA3",
  // Stallion Express APC services
  ApcPriorityWorldwide = "APCPriorityWorldwide",
  ApcPriorityWorldwideTracked = "APCPriorityWorldwideTracked",
  ApcPriorityWorldwideTrackedDdp = "APCPriorityWorldwideTrackedDdp",
  ApcPriorityWorldwideExpedited = "APCPriorityWorldwideExpedited",
  ApcPriorityWorldwideExpeditedDdp = "APCPriorityWorldwideExpeditedDdp",
}

export enum FirstMileServices {
  XParcelGround = "XParcelGround",
  XParcelPriority = "XParcelPriority",
  XParcelExpedited = "XParcelExpedited",
  XParcelExpeditedPlus = "XParcelExpeditedPlus",
}

export enum AustraliaPostServices {
  ParcelPost = "ParcelPost",
  ExpressPost = "ExpressPost",
  International = "International",
  Commercial = "Commercial",
  ParcelPostSignature = "ParcelPostSignature" /** We don't know why, but the docs say "ParcelPost" but actual rates append "ParcelPostSignature" */,
  ExpressPostSignature = "ExpressPostSignature",
  InternationalSignature = "InternationalSignature",
  CommercialSignature = "CommercialSignature",
  EParcel1 = "EParcel1",
  EParcelPostReturns = "EParcelPostReturns",
  ExpressParcelPostReturns = "ExpressParcelPostReturns",
  ExpressPostEParcel = "ExpressPostEParcel",
  InternationalEconomyAirmailParcels = "InternationalEconomyAirmailParcels",
  InternationalEconomyWSODREGPost = "InternationalEconomyWSODREGPost",
  InternationalExpressDocSecIDocs = "InternationalExpressDocSecIDocs",
  InternationalExpressMerchandise = "InternationalExpressMerchandise",
  InternationalExpressMerchECIMerch = "InternationalExpressMerchECIMerch",
  InternationalStandardPackTrack = "InternationalStandardPackTrack",
  ParcelPostExtra = "ParcelPostExtra",
  ParcelPostWinePlusSignature = "ParcelPostWinePlusSignature",
  ParcelPostWineSignature = "ParcelPostWineSignature",
  ExpressEparcelPostReturns = "ExpressEparcelPostReturns",
  EparcelPostReturns = "EparcelPostReturns",
  IntlExpressDocs = "IntlExpressDocs",
  IntlStandardPackTrack = "IntlStandardPackTrack",
  IntLStandardWithSignature = "IntLStandardWithSignature", // The capital L is not a mistake, that's what we get
  IntlExpressMerch = "IntlExpressMerch",
}

// Only AusPost services allowed for RETURN labels; others (e.g. outbound ParcelPost)
// go "unmanifested" and incur penalty fees. EasyPost uses the lowercase-p casing.
export const ALLOWED_AUSTRALIA_POST_RETURN_SERVICES: ReadonlySet<string> =
  new Set<string>([
    AustraliaPostServices.EparcelPostReturns,
    AustraliaPostServices.ExpressParcelPostReturns,
    AustraliaPostServices.ExpressEparcelPostReturns,
  ]);

export function isAllowedAustraliaPostReturnService(service: string): boolean {
  return ALLOWED_AUSTRALIA_POST_RETURN_SERVICES.has(service);
}

export enum CanadaPostServices {
  RegularParcel = "RegularParcel",
  ExpeditedParcel = "ExpeditedParcel",
  Xpresspost = "Xpresspost",
  Priority = "Priority",
  ExpeditedParcelUSA = "ExpeditedParcelUSA",
  SmallPacketUSAAir = "SmallPacketUSAAir",
  TrackedPacketUSA = "TrackedPacketUSA",
  TrackedPacketUSALVM = "TrackedPacketUSALVM",
  XpresspostUSA = "XpresspostUSA",
  XpresspostInternational = "XpresspostInternational",
  InternationalParcelAir = "InternationalParcelAir",
  InternationalParcelSurface = "InternationalParcelSurface",
  SmallPacketInternationalAir = "SmallPacketInternationalAir",
  SmallPacketInternationalSurface = "SmallPacketInternationalSurface",
  TrackedPacketInternational = "TrackedPacketInternational",
  ExpeditedParcelPlus = "ExpeditedParcelPlus",
}

export enum PurolatorServices {
  PurolatorExpress = "PurolatorExpress",
  PurolatorExpress12PM = "PurolatorExpress12PM",
  PurolatorExpressPack12PM = "PurolatorExpressPack12PM",
  PurolatorExpressBox12PM = "PurolatorExpressBox12PM",
  PurolatorExpressEnvelope12PM = "PurolatorExpressEnvelope12PM",
  PurolatorExpress1030AM = "PurolatorExpress1030AM",
  PurolatorExpress9AM = "PurolatorExpress9AM",
  PurolatorExpressBox = "PurolatorExpressBox",
  PurolatorExpressBox1030AM = "PurolatorExpressBox1030AM",
  PurolatorExpressBox9AM = "PurolatorExpressBox9AM",
  PurolatorExpressBoxEvening = "PurolatorExpressBoxEvening",
  PurolatorExpressBoxInternational = "PurolatorExpressBoxInternational",
  PurolatorExpressBoxUS = "PurolatorExpressBoxUS",
  PurolatorExpressEnvelope = "PurolatorExpressEnvelope",
  PurolatorExpressEnvelope1030AM = "PurolatorExpressEnvelope1030AM",
  PurolatorExpressEnvelope9AM = "PurolatorExpressEnvelope9AM",
  PurolatorExpressEnvelopeEvening = "PurolatorExpressEnvelopeEvening",
  PurolatorExpressEnvelopeInternational = "PurolatorExpressEnvelopeInternational",
  PurolatorExpressEnvelopeUS = "PurolatorExpressEnvelopeUS",
  PurolatorExpressEvening = "PurolatorExpressEvening",
  PurolatorExpressInternational = "PurolatorExpressInternational",
  PurolatorExpressInternational1030AM = "PurolatorExpressInternational1030AM",
  PurolatorExpressInternational1200 = "PurolatorExpressInternational1200",
  PurolatorExpressInternational9AM = "PurolatorExpressInternational9AM",
  PurolatorExpressBoxInternational1030AM = "PurolatorExpressBoxInternational1030AM",
  PurolatorExpressBoxInternational1200 = "PurolatorExpressBoxInternational1200",
  PurolatorExpressBoxInternational9AM = "PurolatorExpressBoxInternational9AM",
  PurolatorExpressEnvelopeInternational1030AM = "PurolatorExpressEnvelopeInternational1030AM",
  PurolatorExpressEnvelopeInternational1200 = "PurolatorExpressEnvelopeInternational1200",
  PurolatorExpressEnvelopeInternational9AM = "PurolatorExpressEnvelopeInternational9AM",
  PurolatorExpressPackInternational1030AM = "PurolatorExpressPackInternational1030AM",
  PurolatorExpressPackInternational1200 = "PurolatorExpressPackInternational1200",
  PurolatorExpressPackInternational9AM = "PurolatorExpressPackInternational9AM",
  PurolatorExpressPack = "PurolatorExpressPack",
  PurolatorExpressPack1030AM = "PurolatorExpressPack1030AM",
  PurolatorExpressPack9AM = "PurolatorExpressPack9AM",
  PurolatorExpressPackEvening = "PurolatorExpressPackEvening",
  PurolatorExpressPackInternational = "PurolatorExpressPackInternational",
  PurolatorExpressPackUS = "PurolatorExpressPackUS",
  PurolatorExpressUS = "PurolatorExpressUS",
  PurolatorExpressUS1030AM = "PurolatorExpressUS1030AM",
  PurolatorExpressUS1200 = "PurolatorExpressUS1200",
  PurolatorExpressUS9AM = "PurolatorExpressUS9AM",
  PurolatorExpressBoxUS1030AM = "PurolatorExpressBoxUS1030AM",
  PurolatorExpressBoxUS1200 = "PurolatorExpressBoxUS1200",
  PurolatorExpressBoxUS9AM = "PurolatorExpressBoxUS9AM",
  PurolatorExpressEnvelopeUS1030AM = "PurolatorExpressEnvelopeUS1030AM",
  PurolatorExpressEnvelopeUS1200 = "PurolatorExpressEnvelopeUS1200",
  PurolatorExpressEnvelopeUS9AM = "PurolatorExpressEnvelopeUS9AM",
  PurolatorExpressPackUS1030AM = "PurolatorExpressPackUS1030AM",
  PurolatorExpressPackUS1200 = "PurolatorExpressPackUS1200",
  PurolatorExpressPackUS9AM = "PurolatorExpressPackUS9AM",
  PurolatorGround = "PurolatorGround",
  PurolatorGround1030AM = "PurolatorGround1030AM",
  PurolatorGround9AM = "PurolatorGround9AM",
  PurolatorGround12PM = "PurolatorGround12PM",
  PurolatorGroundDistribution = "PurolatorGroundDistribution",
  PurolatorGroundEvening = "PurolatorGroundEvening",
  PurolatorGroundUS = "PurolatorGroundUS",
  PurolatorQuickShip = "PurolatorQuickShip",
  PurolatorQuickShipEnvelope = "PurolatorQuickShipEnvelope",
  PurolatorQuickShipPack = "PurolatorQuickShipPack",
  PurolatorQuickShipBox = "PurolatorQuickShipBox",
}

export enum GlsServices {
  EarlyPriorityOvernight = "EarlyPriorityOvernight",
  PriorityOvernight = "PriorityOvernight",
  CaliforniaParcelService = "CaliforniaParcelService",
  SaturdayDeliveryService = "SaturdayDeliveryService",
  EarlySaturdayService = "EarlySaturdayService",
  NoonPriorityService = "NoonPriorityService",
}

export enum RoyalMailV3Services {
  RoyalMailV3 = "RoyalMailV3",
  DEImportRoyalMail24PlusParcelsWeekendService = "DEImportRoyalMail24PlusParcelsWeekendService",
  DEImportStandard24LargeLetter = "DEImportStandard24LargeLetter",
  DEImportStandard24LargeLetterWeekendService = "DEImportStandard24LargeLetterWeekendService",
  DEImportStandard24Parcel = "DEImportStandard24Parcel",
  DEImportStandard24ParcelWeekendService = "DEImportStandard24ParcelWeekendService",
  DEImportStandard48LargeLetter = "DEImportStandard48LargeLetter",
  DEImportStandard48LargeLetterWeekendService = "DEImportStandard48LargeLetterWeekendService",
  DEImportTracked24ParcelBoxableHighVolumeWeekendService = "DEImportTracked24ParcelBoxableHighVolumeWeekendService",
  DEImportTracked24ParcelBoxableWeekendService = "DEImportTracked24ParcelBoxableWeekendService",
  DEImportTracked24ParcelHighVolumeWeekendService = "DEImportTracked24ParcelHighVolumeWeekendService",
  DEImportTracked24SignatureParcelHighVolumeWeekendServiceAGE = "DEImportTracked24SignatureParcelHighVolumeWeekendServiceAGE",
  DEImportTracked48ParcelBoxableHighVolumeWeekendService = "DEImportTracked48ParcelBoxableHighVolumeWeekendService",
  DEImportTracked48ParcelBoxableWeekendService = "DEImportTracked48ParcelBoxableWeekendService",
  DEImportTracked48ParcelHighVolumeWeekendService = "DEImportTracked48ParcelHighVolumeWeekendService",
  DEImportTracked48SignatureParcelHighVolumeAGE = "DEImportTracked48SignatureParcelHighVolumeAGE",
  DEImportTracked48SignatureParcelHighVolumeWeekendServiceAGE = "DEImportTracked48SignatureParcelHighVolumeWeekendServiceAGE",
  DEImporttoEUMaxSortLargeLetter = "DEImporttoEUMaxSortLargeLetter",
  DEImporttoEUMaxSortParcel = "DEImporttoEUMaxSortParcel",
  DEImporttoEUSignedParcel = "DEImporttoEUSignedParcel",
  DEImporttoEUTrackedHighVolLargeLetter = "DEImporttoEUTrackedHighVolLargeLetter",
  DEImporttoEUTrackedParcel = "DEImporttoEUTrackedParcel",
  DEImporttoEUTrackedSignedLargeLetter = "DEImporttoEUTrackedSignedLargeLetter",
  DEImporttoEUTrackedSignedParcel = "DEImporttoEUTrackedSignedParcel",
  HMForcesMail = "HMForcesMail",
  HMForcesSignedFor = "HMForcesSignedFor",
  HMForcesSpecialDelivery1000 = "HMForcesSpecialDelivery1000",
  HMForcesSpecialDelivery2500 = "HMForcesSpecialDelivery2500",
  HMForcesSpecialDelivery500 = "HMForcesSpecialDelivery500",
  ImportDETracked24 = "ImportDETracked24",
  ImportDETracked24HighVolume = "ImportDETracked24HighVolume",
  ImportDETracked24Letterboxable = "ImportDETracked24Letterboxable",
  ImportDETracked24LetterboxableHighVolume = "ImportDETracked24LetterboxableHighVolume",
  ImportDETracked24SignatureHighVolumeAGE = "ImportDETracked24SignatureHighVolumeAGE",
  ImportDETracked24SignatureStandardAGE = "ImportDETracked24SignatureStandardAGE",
  ImportDETracked48 = "ImportDETracked48",
  ImportDETracked48HighVolume = "ImportDETracked48HighVolume",
  ImportDETracked48Letterboxable = "ImportDETracked48Letterboxable",
  ImportDETracked48LetterboxableHighVolume = "ImportDETracked48LetterboxableHighVolume",
  ImportDETrackedReturns24 = "ImportDETrackedReturns24",
  ImportDETrackedReturns48 = "ImportDETrackedReturns48",
  InternationalBusinessMailLargeLetterCountryPricedPriority = "InternationalBusinessMailLargeLetterCountryPricedPriority",
  InternationalBusinessMailLargeLetterCountryPricedPriorityETOE = "InternationalBusinessMailLargeLetterCountryPricedPriorityETOE",
  InternationalBusinessMailLargeLetterCountrySortPriority = "InternationalBusinessMailLargeLetterCountrySortPriority",
  InternationalBusinessMailLargeLetterMaxSortPriorityService = "InternationalBusinessMailLargeLetterMaxSortPriorityService",
  InternationalBusinessMailLargeLetterMaxSortResidueStandard = "InternationalBusinessMailLargeLetterMaxSortResidueStandard",
  InternationalBusinessMailLargeLetterMaxSortStandardService = "InternationalBusinessMailLargeLetterMaxSortStandardService",
  InternationalBusinessMailLargeLetterSemiTracked = "InternationalBusinessMailLargeLetterSemiTracked",
  InternationalBusinessMailLargeLetterZeroSortPriority = "InternationalBusinessMailLargeLetterZeroSortPriority",
  InternationalBusinessMailLargeLetterZeroSortPriorityMachine = "InternationalBusinessMailLargeLetterZeroSortPriorityMachine",
  InternationalBusinessMailLargeLetterZoneSortPriority = "InternationalBusinessMailLargeLetterZoneSortPriority",
  InternationalBusinessMailLargeLetterZoneSortPriorityMachine = "InternationalBusinessMailLargeLetterZoneSortPriorityMachine",
  InternationalBusinessMailLetterMaxSortResidueStandard = "InternationalBusinessMailLetterMaxSortResidueStandard",
  InternationalBusinessMailLettersMaxSortStandard = "InternationalBusinessMailLettersMaxSortStandard",
  InternationalBusinessMailLettersZeroSortPriority = "InternationalBusinessMailLettersZeroSortPriority",
  InternationalBusinessMailLettersZoneSortPriority = "InternationalBusinessMailLettersZoneSortPriority",
  InternationalBusinessMailSignedCountryPriced = "InternationalBusinessMailSignedCountryPriced",
  InternationalBusinessMailSignedExtraCompensationCountryPriced = "InternationalBusinessMailSignedExtraCompensationCountryPriced",
  InternationalBusinessMailSignedHighVolCountryPriced = "InternationalBusinessMailSignedHighVolCountryPriced",
  InternationalBusinessMailSignedZoneSort = "InternationalBusinessMailSignedZoneSort",
  InternationalBusinessMailTrackedCountryPriced = "InternationalBusinessMailTrackedCountryPriced",
  InternationalBusinessMailTrackedHighVolCountryPriced = "InternationalBusinessMailTrackedHighVolCountryPriced",
  InternationalBusinessMailTrackedHighVolExtraCompCountryPriced = "InternationalBusinessMailTrackedHighVolExtraCompCountryPriced",
  InternationalBusinessMailTrackedLargeLetterCountryPricedETOE = "InternationalBusinessMailTrackedLargeLetterCountryPricedETOE",
  InternationalBusinessMailTrackedSignedCountryPriced = "InternationalBusinessMailTrackedSignedCountryPriced",
  InternationalBusinessMailTrackedSignedHighVolCountryPriced = "InternationalBusinessMailTrackedSignedHighVolCountryPriced",
  InternationalBusinessMailTrackedSignedHighVolExtraCompCountryPriced = "InternationalBusinessMailTrackedSignedHighVolExtraCompCountryPriced",
  InternationalBusinessMailTrackedSignedLargeLetterCountryPricedETOE = "InternationalBusinessMailTrackedSignedLargeLetterCountryPricedETOE",
  InternationalBusinessMailTrackedSignedZoneSort = "InternationalBusinessMailTrackedSignedZoneSort",
  InternationalBusinessMailTrackedZoneSort = "InternationalBusinessMailTrackedZoneSort",
  InternationalBusinessPCTrackedSignedLargeLetterHVExtraCompCountryPricedETOE = "InternationalBusinessPCTrackedSignedLargeLetterHVExtraCompCountryPricedETOE",
  InternationalBusinessParcelPriorityCountryPricedBoxable = "InternationalBusinessParcelPriorityCountryPricedBoxable",
  InternationalBusinessParcelPriorityCountryPricedBoxableDDP = "InternationalBusinessParcelPriorityCountryPricedBoxableDDP",
  InternationalBusinessParcelTrackedCountryPriced = "InternationalBusinessParcelTrackedCountryPriced",
  InternationalBusinessParcelTrackedCountryPricedBoxable = "InternationalBusinessParcelTrackedCountryPricedBoxable",
  InternationalBusinessParcelTrackedCountryPricedBoxableDDP = "InternationalBusinessParcelTrackedCountryPricedBoxableDDP",
  InternationalBusinessParcelTrackedCountryPricedBoxableExtraComp = "InternationalBusinessParcelTrackedCountryPricedBoxableExtraComp",
  InternationalBusinessParcels = "InternationalBusinessParcels",
  InternationalBusinessParcelsPrintDirectPriority = "InternationalBusinessParcelsPrintDirectPriority",
  InternationalBusinessParcelsPrintDirectStandard = "InternationalBusinessParcelsPrintDirectStandard",
  InternationalBusinessParcelsSignedCountryPriced = "InternationalBusinessParcelsSignedCountryPriced",
  InternationalBusinessParcelsSignedExtraCompensationCountryPriced = "InternationalBusinessParcelsSignedExtraCompensationCountryPriced",
  InternationalBusinessParcelsSignedExtraCompensationZoneSort = "InternationalBusinessParcelsSignedExtraCompensationZoneSort",
  InternationalBusinessParcelsSignedZoneSort = "InternationalBusinessParcelsSignedZoneSort",
  InternationalBusinessParcelsTrackedCountryPriced = "InternationalBusinessParcelsTrackedCountryPriced",
  InternationalBusinessParcelsTrackedCountryPricedETOE = "InternationalBusinessParcelsTrackedCountryPricedETOE",
  InternationalBusinessParcelsTrackedDirectIrelandCountry = "InternationalBusinessParcelsTrackedDirectIrelandCountry",
  InternationalBusinessParcelsTrackedExtraCompCountryPriced = "InternationalBusinessParcelsTrackedExtraCompCountryPriced",
  InternationalBusinessParcelsTrackedExtraCompZoneSort = "InternationalBusinessParcelsTrackedExtraCompZoneSort",
  InternationalBusinessParcelsTrackedSignedCountryPriced = "InternationalBusinessParcelsTrackedSignedCountryPriced",
  InternationalBusinessParcelsTrackedSignedCountryPricedETOE = "InternationalBusinessParcelsTrackedSignedCountryPricedETOE",
  InternationalBusinessParcelsTrackedSignedDDP = "InternationalBusinessParcelsTrackedSignedDDP",
  InternationalBusinessParcelsTrackedSignedExtraCompensationCountryPriced = "InternationalBusinessParcelsTrackedSignedExtraCompensationCountryPriced",
  InternationalBusinessParcelsTrackedSignedExtraCompensationZoneSort = "InternationalBusinessParcelsTrackedSignedExtraCompensationZoneSort",
  InternationalBusinessParcelsTrackedSignedZoneSort = "InternationalBusinessParcelsTrackedSignedZoneSort",
  InternationalBusinessParcelsTrackedZoneSort = "InternationalBusinessParcelsTrackedZoneSort",
  InternationalBusinessParcelsZeroSortPriority = "InternationalBusinessParcelsZeroSortPriority",
  InternationalBusinessParcelsZeroSortPriorityETOE = "InternationalBusinessParcelsZeroSortPriorityETOE",
  InternationalBusinessParcelsZoneSortPriorityService = "InternationalBusinessParcelsZoneSortPriorityService",
  InternationalBusinessPersonalCorrespondenceLargeLetterCountrySortPriorityETOE = "InternationalBusinessPersonalCorrespondenceLargeLetterCountrySortPriorityETOE",
  InternationalBusinessPersonalCorrespondenceLetterPriorityUntracked = "InternationalBusinessPersonalCorrespondenceLetterPriorityUntracked",
  InternationalBusinessPersonalCorrespondenceLetterPriorityUntrackedETOE = "InternationalBusinessPersonalCorrespondenceLetterPriorityUntrackedETOE",
  InternationalBusinessPersonalCorrespondenceLetterTrackedHighVolCountryPricedETOE = "InternationalBusinessPersonalCorrespondenceLetterTrackedHighVolCountryPricedETOE",
  InternationalBusinessPersonalCorrespondenceLetterTrackedSignedHighVolCountryPricedETOE = "InternationalBusinessPersonalCorrespondenceLetterTrackedSignedHighVolCountryPricedETOE",
  InternationalBusinessPersonalCorrespondenceMaxSortLetter = "InternationalBusinessPersonalCorrespondenceMaxSortLetter",
  InternationalBusinessPersonalCorrespondenceMaxSortResidueLargeLetter = "InternationalBusinessPersonalCorrespondenceMaxSortResidueLargeLetter",
  InternationalBusinessPersonalCorrespondenceMaxSortResidueLetter = "InternationalBusinessPersonalCorrespondenceMaxSortResidueLetter",
  InternationalBusinessPersonalCorrespondenceSignedLargeLetterExtraCompensationCountryPricedETOE = "InternationalBusinessPersonalCorrespondenceSignedLargeLetterExtraCompensationCountryPricedETOE",
  InternationalBusinessPersonalCorrespondenceSignedLetterHighVolCountryPricedETOE = "InternationalBusinessPersonalCorrespondenceSignedLetterHighVolCountryPricedETOE",
  InternationalBusinessPersonalCorrespondenceTrackedLargeLetterHighVolExtraCompCountryPricedETOE = "InternationalBusinessPersonalCorrespondenceTrackedLargeLetterHighVolExtraCompCountryPricedETOE",
  InternationalBusinessPrintedMatterPacket = "InternationalBusinessPrintedMatterPacket",
  InternationalBusinessTrackedExpressNPC = "InternationalBusinessTrackedExpressNPC",
  InternationalEconomyOnAccount = "InternationalEconomyOnAccount",
  InternationalSignedOnAccount = "InternationalSignedOnAccount",
  InternationalSignedOnAccountExtraComp = "InternationalSignedOnAccountExtraComp",
  InternationalStandardOnAccount = "InternationalStandardOnAccount",
  InternationalTrackedOnAccount = "InternationalTrackedOnAccount",
  InternationalTrackedOnAccountExtraComp = "InternationalTrackedOnAccountExtraComp",
  InternationalTrackedParcels30kg = "InternationalTrackedParcels30kg",
  InternationalTrackedParcels30kgCPrio = "InternationalTrackedParcels30kgCPrio",
  InternationalTrackedParcels30kgETOEC = "InternationalTrackedParcels30kgETOEC",
  InternationalTrackedParcels30kgETOEE = "InternationalTrackedParcels30kgETOEE",
  InternationalTrackedParcels30kgExtraComp = "InternationalTrackedParcels30kgExtraComp",
  InternationalTrackedParcels30kgExtraCompETOEC = "InternationalTrackedParcels30kgExtraCompETOEC",
  InternationalTrackedParcels30kgExtraCompETOEE = "InternationalTrackedParcels30kgExtraCompETOEE",
  InternationalTrackedParcels30kgXCompCPrio = "InternationalTrackedParcels30kgXCompCPrio",
  InternationalTrackedSignedOnAccount = "InternationalTrackedSignedOnAccount",
  InternationalTrackedSignedOnAccountExtraComp = "InternationalTrackedSignedOnAccountExtraComp",
  ParcelpostFlatRateAnnual = "ParcelpostFlatRateAnnual",
  RM24LargeLetterAnnualFlatRate = "RM24LargeLetterAnnualFlatRate",
  RM24PresortedLargeLetterAnnualFlatRate = "RM24PresortedLargeLetterAnnualFlatRate",
  RM24PresortedParcelsAnnualFlatRate = "RM24PresortedParcelsAnnualFlatRate",
  RM48LargeLetterAnnualFlatRate = "RM48LargeLetterAnnualFlatRate",
  RM48PresortedLargeLetterAnnualFlatRate = "RM48PresortedLargeLetterAnnualFlatRate",
  RM48PresortedParcelsAnnualFlatRate = "RM48PresortedParcelsAnnualFlatRate",
  RoyalMail1stClass = "RoyalMail1stClass",
  RoyalMail1stClassLettersDailyRateservice = "RoyalMail1stClassLettersDailyRateservice",
  RoyalMail1stClassSignedFor = "RoyalMail1stClassSignedFor",
  RoyalMail24LargeLetterDailyRate = "RoyalMail24LargeLetterDailyRate",
  RoyalMail24LargeLetterFlatRate = "RoyalMail24LargeLetterFlatRate",
  RoyalMail24LargeLetterFlatRateService = "RoyalMail24LargeLetterFlatRateService",
  RoyalMail24PacketpostFlatRateService = "RoyalMail24PacketpostFlatRateService",
  RoyalMail24ParcelDailyRateService = "RoyalMail24ParcelDailyRateService",
  RoyalMail24ParcelSort8DailyRateService = "RoyalMail24ParcelSort8DailyRateService",
  RoyalMail24ParcelSort8FlatRateService = "RoyalMail24ParcelSort8FlatRateService",
  RoyalMail24ParcelsDailyRate = "RoyalMail24ParcelsDailyRate",
  RoyalMail24ParcelsFlatRate = "RoyalMail24ParcelsFlatRate",
  RoyalMail24PresortedLargeLetter = "RoyalMail24PresortedLargeLetter",
  RoyalMail24PresortedParcels = "RoyalMail24PresortedParcels",
  RoyalMail24SORT8LargeLetterAnnualFlatRate = "RoyalMail24SORT8LargeLetterAnnualFlatRate",
  RoyalMail24SORT8ParcelsAnnualFlatRate = "RoyalMail24SORT8ParcelsAnnualFlatRate",
  RoyalMail2ndClass = "RoyalMail2ndClass",
  RoyalMail2ndClassLettersDailyRateservice = "RoyalMail2ndClassLettersDailyRateservice",
  RoyalMail2ndClassSignedFor = "RoyalMail2ndClassSignedFor",
  RoyalMail48LargeLetterDailyRate = "RoyalMail48LargeLetterDailyRate",
  RoyalMail48LargeLetterFlatRate = "RoyalMail48LargeLetterFlatRate",
  RoyalMail48LargeLetterFlatRateService = "RoyalMail48LargeLetterFlatRateService",
  RoyalMail48PacketpostFlatRateService = "RoyalMail48PacketpostFlatRateService",
  RoyalMail48ParcelDailyRateService = "RoyalMail48ParcelDailyRateService",
  RoyalMail48ParcelSort8DailyRateService = "RoyalMail48ParcelSort8DailyRateService",
  RoyalMail48ParcelSort8FlatRateService = "RoyalMail48ParcelSort8FlatRateService",
  RoyalMail48ParcelsDailyRate = "RoyalMail48ParcelsDailyRate",
  RoyalMail48ParcelsFlatRate = "RoyalMail48ParcelsFlatRate",
  RoyalMail48PresortedLargeLetter = "RoyalMail48PresortedLargeLetter",
  RoyalMail48PresortedParcels = "RoyalMail48PresortedParcels",
  RoyalMail48SORT8LargeLetterAnnualFlatRate = "RoyalMail48SORT8LargeLetterAnnualFlatRate",
  RoyalMail48Sort8ParcelsAnnualFlatRate = "RoyalMail48Sort8ParcelsAnnualFlatRate",
  SpecialDeliveryGuaranteed1000 = "SpecialDeliveryGuaranteed1000",
  SpecialDeliveryGuaranteed2500 = "SpecialDeliveryGuaranteed2500",
  SpecialDeliveryGuaranteed750 = "SpecialDeliveryGuaranteed750",
  SpecialDeliveryGuaranteedAGE1000 = "SpecialDeliveryGuaranteedAGE1000",
  SpecialDeliveryGuaranteedAGE2500 = "SpecialDeliveryGuaranteedAGE2500",
  SpecialDeliveryGuaranteedAGE750 = "SpecialDeliveryGuaranteedAGE750",
  SpecialDeliveryGuaranteedBy1PM1000 = "SpecialDeliveryGuaranteedBy1PM1000",
  SpecialDeliveryGuaranteedBy1PM2500 = "SpecialDeliveryGuaranteedBy1PM2500",
  SpecialDeliveryGuaranteedBy1PM750 = "SpecialDeliveryGuaranteedBy1PM750",
  SpecialDeliveryGuaranteedBy1PMAGE1000 = "SpecialDeliveryGuaranteedBy1PMAGE1000",
  SpecialDeliveryGuaranteedBy1PMAGE2500 = "SpecialDeliveryGuaranteedBy1PMAGE2500",
  SpecialDeliveryGuaranteedBy1PMAGE750 = "SpecialDeliveryGuaranteedBy1PMAGE750",
  SpecialDeliveryGuaranteedBy1PMID1000 = "SpecialDeliveryGuaranteedBy1PMID1000",
  SpecialDeliveryGuaranteedBy1PMID2500 = "SpecialDeliveryGuaranteedBy1PMID2500",
  SpecialDeliveryGuaranteedBy1PMID750 = "SpecialDeliveryGuaranteedBy1PMID750",
  SpecialDeliveryGuaranteedBy9AM1000 = "SpecialDeliveryGuaranteedBy9AM1000",
  SpecialDeliveryGuaranteedBy9AM2500 = "SpecialDeliveryGuaranteedBy9AM2500",
  SpecialDeliveryGuaranteedBy9AM750 = "SpecialDeliveryGuaranteedBy9AM750",
  SpecialDeliveryGuaranteedBy9AMAGE1000 = "SpecialDeliveryGuaranteedBy9AMAGE1000",
  SpecialDeliveryGuaranteedBy9AMAGE2500 = "SpecialDeliveryGuaranteedBy9AMAGE2500",
  SpecialDeliveryGuaranteedBy9AMAGE750 = "SpecialDeliveryGuaranteedBy9AMAGE750",
  SpecialDeliveryGuaranteedBy9AMID1000 = "SpecialDeliveryGuaranteedBy9AMID1000",
  SpecialDeliveryGuaranteedBy9AMID2500 = "SpecialDeliveryGuaranteedBy9AMID2500",
  SpecialDeliveryGuaranteedBy9AMID750 = "SpecialDeliveryGuaranteedBy9AMID750",
  SpecialDeliveryGuaranteedID1000 = "SpecialDeliveryGuaranteedID1000",
  SpecialDeliveryGuaranteedID2500 = "SpecialDeliveryGuaranteedID2500",
  SpecialDeliveryGuaranteedID750 = "SpecialDeliveryGuaranteedID750",
  Tracked24 = "Tracked24",
  Tracked24HighVolume = "Tracked24HighVolume",
  Tracked24HighVolumeSignatureAGE = "Tracked24HighVolumeSignatureAGE",
  Tracked24SignatureAGE = "Tracked24SignatureAGE",
  Tracked48 = "Tracked48",
  Tracked48HighVolume = "Tracked48HighVolume",
  Tracked48HighVolumeSignatureAGE = "Tracked48HighVolumeSignatureAGE",
  Tracked48SignatureAGE = "Tracked48SignatureAGE",
  TrackedLetterBoxable24 = "TrackedLetterBoxable24",
  TrackedLetterBoxable24HighVolume = "TrackedLetterBoxable24HighVolume",
  TrackedLetterBoxable48 = "TrackedLetterBoxable48",
  TrackedLetterBoxable48HighVolume = "TrackedLetterBoxable48HighVolume",
  TrackedReturns24 = "TrackedReturns24",
  TrackedReturns48 = "TrackedReturns48",
}

export enum AsendiaServices {
  ADS = "ADS",
  AirFreightInbound = "AirFreightInbound",
  AirFreightOutbound = "AirFreightOutbound",
  AsendiaDomesticBoundPrinterMatterExpedited = "AsendiaDomesticBoundPrinterMatterExpedited",
  AsendiaDomesticBoundPrinterMatterGround = "AsendiaDomesticBoundPrinterMatterGround",
  AsendiaDomesticFlatsExpedited = "AsendiaDomesticFlatsExpedited",
  AsendiaDomesticFlatsGround = "AsendiaDomesticFlatsGround",
  AsendiaDomesticParcelGroundOver1lb = "AsendiaDomesticParcelGroundOver1lb",
  AsendiaDomesticParcelGroundUnder1lb = "AsendiaDomesticParcelGroundUnder1lb",
  AsendiaDomesticParcelMAXOver1lb = "AsendiaDomesticParcelMAXOver1lb",
  AsendiaDomesticParcelMAXUnder1lb = "AsendiaDomesticParcelMAXUnder1lb",
  AsendiaDomesticParcelOver1lbExpedited = "AsendiaDomesticParcelOver1lbExpedited",
  AsendiaDomesticParcelUnder1lbExpedited = "AsendiaDomesticParcelUnder1lbExpedited",
  AsendiaDomesticPromoParcelExpedited = "AsendiaDomesticPromoParcelExpedited",
  AsendiaDomesticPromoParcelGround = "AsendiaDomesticPromoParcelGround",
  BulkFreight = "BulkFreight",
  BusinessMailCanadaLettermail = "BusinessMailCanadaLettermail",
  BusinessMailCanadaLettermailMachineable = "BusinessMailCanadaLettermailMachineable",
  BusinessMailEconomy = "BusinessMailEconomy",
  BusinessMailEconomyLPWholesale = "BusinessMailEconomyLPWholesale",
  BusinessMailEconomySPWholesale = "BusinessMailEconomySPWholesale",
  BusinessMailIPA = "BusinessMailIPA",
  BusinessMailISAL = "BusinessMailISAL",
  BusinessMailPriority = "BusinessMailPriority",
  BusinessMailPriorityLPWholesale = "BusinessMailPriorityLPWholesale",
  BusinessMailPrioritySPWholesale = "BusinessMailPrioritySPWholesale",
  MarketingMailCanadaPersonalizedLCP = "MarketingMailCanadaPersonalizedLCP",
  MarketingMailCanadaPersonalizedMachineable = "MarketingMailCanadaPersonalizedMachineable",
  MarketingMailCanadaPersonalizedNDG = "MarketingMailCanadaPersonalizedNDG",
  MarketingMailEconomy = "MarketingMailEconomy",
  MarketingMailIPA = "MarketingMailIPA",
  MarketingMailISAL = "MarketingMailISAL",
  MarketingMailPriority = "MarketingMailPriority",
  PublicationsCanadaLCP = "PublicationsCanadaLCP",
  PublicationsCanadaNDG = "PublicationsCanadaNDG",
  PublicationsEconomy = "PublicationsEconomy",
  PublicationsIPA = "PublicationsIPA",
  PublicationsISAL = "PublicationsISAL",
  PublicationsPriority = "PublicationsPriority",
  ePAQElite = "ePAQElite",
  ePAQEliteCustom = "ePAQEliteCustom",
  ePAQEliteDAP = "ePAQEliteDAP",
  ePAQEliteDDP = "ePAQEliteDDP",
  ePAQEliteDDPOversized = "ePAQEliteDDPOversized",
  ePAQEliteDPD = "ePAQEliteDPD",
  ePAQEliteDirectAccessCanadaDDP = "ePAQEliteDirectAccessCanadaDDP",
  ePAQEliteOversized = "ePAQEliteOversized",
  ePAQPlus = "ePAQPlus",
  ePAQPlusCustom = "ePAQPlusCustom",
  ePAQPlusCustomsPrepaid = "ePAQPlusCustomsPrepaid",
  ePAQPlusDAP = "ePAQPlusDAP",
  ePAQPlusDDP = "ePAQPlusDDP",
  ePAQPlusEconomy = "ePAQPlusEconomy",
  ePAQPlusWholesale = "ePAQPlusWholesale",
  ePAQPlusePacket = "ePAQPlusePacket",
  ePAQPlusePacketCanadaCustomsPrePaid = "ePAQPlusePacketCanadaCustomsPrePaid",
  ePAQPlusePacketCanadaDDP = "ePAQPlusePacketCanadaDDP",
  ePAQReturnsDomestic = "ePAQReturnsDomestic",
  ePAQReturnsInternational = "ePAQReturnsInternational",
  ePAQSelect = "ePAQSelect",
  ePAQSelectCustom = "ePAQSelectCustom",
  ePAQSelectCustomsPrepaidByShopper = "ePAQSelectCustomsPrepaidByShopper",
  ePAQSelectDAP = "ePAQSelectDAP",
  ePAQSelectDDP = "ePAQSelectDDP",
  ePAQSelectDDPDirectAccess = "ePAQSelectDDPDirectAccess",
  ePAQSelectDirectAccess = "ePAQSelectDirectAccess",
  ePAQSelectDirectAccessCanadaDDP = "ePAQSelectDirectAccessCanadaDDP",
  ePAQSelectEconomy = "ePAQSelectEconomy",
  ePAQSelectOversized = "ePAQSelectOversized",
  ePAQSelectOversizedDDP = "ePAQSelectOversizedDDP",
  ePAQSelectPMEI = "ePAQSelectPMEI",
  ePAQSelectPMEICanadaCustomsPrePaid = "ePAQSelectPMEICanadaCustomsPrePaid",
  ePAQSelectPMEIPCPostage = "ePAQSelectPMEIPCPostage",
  ePAQSelectPMI = "ePAQSelectPMI",
  ePAQSelectPMICanadaCustomsPrepaid = "ePAQSelectPMICanadaCustomsPrepaid",
  ePAQSelectPMICanadaDDP = "ePAQSelectPMICanadaDDP",
  ePAQSelectPMINonPresort = "ePAQSelectPMINonPresort",
  ePAQSelectPMIPCPostage = "ePAQSelectPMIPCPostage",
  ePAQStandard = "ePAQStandard",
  ePAQStandardCustom = "ePAQStandardCustom",
  ePAQStandardEconomy = "ePAQStandardEconomy",
  ePAQStandardIPA = "ePAQStandardIPA",
  ePAQStandardISAL = "ePAQStandardISAL",
  ePaqSelectPMEINonPresort = "ePaqSelectPMEINonPresort",
  ePAQSelectDDPDG = "ePAQSelectDDPDG",
  AsendiaDomesticParcelMaxUnder70lb = "AsendiaDomesticParcelMaxUnder70lb",
}

export enum PassportGlobalServices {
  PriorityDdpDelcon = "PriorityDdpDelcon",
  PriorityDdp = "PriorityDdp",
  epacketDdp = "epacketDdp",
  ExpressDdp = "ExpressDdp",
  PriorityDduDelcon = "PriorityDduDelcon",
  PriorityDdu = "PriorityDdu",
  ExpeditedDdpDelcon = "ExpeditedDdpDelcon",
  ExpressDdpDelcon = "ExpressDdpDelcon",
  GlobalPlus = "GlobalPlus",
}

export enum EPostGlobalServices {
  PriorityParcelDeliveryConfirmationDDP = "PriorityParcelDeliveryConfirmationDDP",
  PriorityParcelDeliveryConfirmationDDU = "PriorityParcelDeliveryConfirmationDDU",
}

export enum SpeeDeeServices {
  SpeeDeeDelivery = "SpeeDeeDelivery",
}

export enum UniUniServices {
  Standard = "standard",
}

export enum StallionExpressServices {
  StallionEconomyUsa = "stallion_economy_usa",
  StallionEconomyCa = "stallion_economy_ca",
  UsEconomy = "us_economy",
  UsRegional = "us_regional",
  PrecisionGround = "precision_ground",
}

export enum ChitChatsServices {
  ChitChatsUsEdge = "chit_chats_us_edge",
  ChitChatsUsConnect = "chit_chats_us_connect",
  ChitChatsUsSelect = "chit_chats_us_select",
  ChitChatsSelect = "chit_chats_select",
  ChitChatsSlim = "chit_chats_slim",
  ChitChatsCanadaTracked = "chit_chats_canada_tracked",
  ChitChatsInternationalTracked = "chit_chats_international_tracked",
}

export enum PostNLServices {
  InternationalPacket = "PostNLInternationalPacket",
  InternationalPacketTracked = "PostNLInternationalPacketTracked",
}

export enum IntelcomServices {
  Standard = "IntelcomStandard",
}

export enum FleetOpticsServices {
  Express = "FleetOpticsExpress",
  Ground = "FleetOpticsGround",
}

export enum IcsCourierServices {
  Express = "IcsExpress",
  Ground = "IcsGround",
}

export enum WhizDeliveryServices {
  Standard = "WhizDeliveryStandard",
  NextDay = "WhizDeliveryNextDay",
}

export enum TiktokShopServices {
  CollectionByTiktok = "TiktokShopCollectionByTiktok",
}

export enum ETAExpressServices {
  ETAExpress = "ETAExpress",
}

export enum LandmarkGlobalServices {
  Delivery = "Delivery", // Generic service for Landmark Global
}

export enum OnTracServices {
  Ground = "GRND",
}

export enum GlobalEServices {
  Delivery = "Delivery", // Generic service for Global-E
}

export enum UpsMailInnovationsServices {
  StandardFlats = "StandardFlats",
  ParcelSelectLightweightIrreg = "ParcelSelectLightweight-Irreg",
  ParcelSelect = "ParcelSelect",
  BPMParcels = "BPMParcels",
  BPMFlats = "BPMFlats",
  MediaMail = "MediaMail",
  MarketingParcels = "MarketingParcels",
  EconomyMailInnovations = "EconomyMailinnovations",
  ExpeditedMailInnovations = "ExpeditedMailinnovations",
  First = "First",
  Priority = "Priority",
  PriorityMailInnovations = "PriorityMailinnovations",
  SingleReturns = "SingleReturns",
}

export enum CanparServices {
  Ground = "CanparGround",
  SelectLetter = "CanparSelectLetter",
  SelectPak = "CanparSelectPak",
  Select = "CanparSelect",
  OvernightLetter = "CanparOvernightLetter",
  OvernightPak = "CanparOvernightPak",
  Overnight = "CanparOvernight",
  USALetter = "CanparUSALetter",
  USAPak = "CanparUSAPak",
  SelectUSA = "CanparSelectUSA",
}

export enum ServiceLevel {
  STANDARD = "Standard",
  PLUS = "Plus",
  EXPRESS = "Express",
}

const ServiceSchema_ = z.union([
  createEnum(FedexServices, { enumName: "FedexServices" }),
  createEnum(UpsServices, { enumName: "UpsServices" }),
  createEnum(UspsServices, { enumName: "UspsServices" }),
  createEnum(AmazonServices, { enumName: "AmazonServices" }),
  createEnum(DhlExpressServices, { enumName: "DhlExpressServices" }),
  createEnum(VehoServices, { enumName: "VehoServices" }),
  createEnum(ApcServices, { enumName: "ApcServices" }),
  createEnum(FirstMileServices, { enumName: "FirstMileServices" }),
  createEnum(AustraliaPostServices, { enumName: "AustraliaPostServices" }),
  createEnum(CanadaPostServices, { enumName: "CanadaPostServices" }),
  createEnum(PurolatorServices, { enumName: "PurolatorServices" }),
  createEnum(DhlEcsServices, { enumName: "DhlEcsServices" }),
  createEnum(GlsServices, { enumName: "GlsServices" }),
  createEnum(AsendiaServices, { enumName: "AsendiaServices" }),
  createEnum(RoyalMailV3Services, { enumName: "RoyalMailV3Services" }),
  createEnum(SpeeDeeServices, { enumName: "SpeeDeeServices" }),
  createEnum(PassportGlobalServices, { enumName: "PassportGlobalServices" }),
  createEnum(EPostGlobalServices, { enumName: "EPostGlobalServices" }),
  createEnum(LandmarkGlobalServices, { enumName: "LandmarkGlobalServices" }),
  createEnum(ETAExpressServices, { enumName: "ETAExpressServices" }),
  createEnum(OnTracServices, { enumName: "OnTracServices" }),
  createEnum(GlobalEServices, { enumName: "GlobalEServices" }),
  createEnum(UpsMailInnovationsServices, {
    enumName: "UpsMailInnovationsServices",
  }),
  createEnum(UniUniServices, { enumName: "UniUniServices" }),
  createEnum(StallionExpressServices, { enumName: "StallionExpressServices" }),
  createEnum(ChitChatsServices, { enumName: "ChitChatsServices" }),
  createEnum(PostNLServices, { enumName: "PostNLServices" }),
  createEnum(IntelcomServices, { enumName: "IntelcomServices" }),
  createEnum(FleetOpticsServices, { enumName: "FleetOpticsServices" }),
  createEnum(IcsCourierServices, { enumName: "IcsCourierServices" }),
  createEnum(WhizDeliveryServices, { enumName: "WhizDeliveryServices" }),
  createEnum(TiktokShopServices, { enumName: "TiktokShopServices" }),
  createEnum(CanparServices, { enumName: "CanparServices" }),
]);
export type Service = z.infer<typeof ServiceSchema_>;

const CorrelatedCarrierServiceSchema_ = z.union([
  z.object({
    carrier: createEnum([Carriers.FEDEX, Carriers.FEDEX_SMART_POST], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(FedexServices, { enumName: "FedexServices" }),
  }),
  z.object({
    carrier: createEnum([Carriers.UPS, Carriers.UPS_SUREPOST], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(UpsServices, { enumName: "UpsServices" }),
  }),
  z.object({
    carrier: createEnum(
      [Carriers.USPS, Carriers.USPS_RETURNS, Carriers.USPS_SHIP],
      { enumName: "Carriers", isPartial: true, originalEnum: Carriers },
    ),
    service: createEnum(UspsServices, { enumName: "UspsServices" }),
  }),
  z.object({
    carrier: createEnum([Carriers.AMAZON], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(AmazonServices, { enumName: "AmazonServices" }),
  }),
  z.object({
    carrier: createEnum([Carriers.DHL_EXPRESS], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(DhlExpressServices, { enumName: "DhlExpressServices" }),
  }),
  z.object({
    carrier: createEnum([Carriers.VEHO], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(VehoServices, { enumName: "VehoServices" }),
  }),
  z.object({
    carrier: createEnum([Carriers.APC], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(ApcServices, { enumName: "ApcServices" }),
  }),
  z.object({
    carrier: createEnum([Carriers.FIRST_MILE], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(FirstMileServices, { enumName: "FirstMileServices" }),
  }),
  z.object({
    carrier: createEnum([Carriers.DHL_ECS], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(DhlEcsServices, { enumName: "DhlEcsServices" }),
  }),
  z.object({
    carrier: createEnum([Carriers.AUSTRALIA_POST], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(AustraliaPostServices, {
      enumName: "AustraliaPostServices",
    }),
  }),
  z.object({
    carrier: createEnum([Carriers.CANADA_POST], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(CanadaPostServices, { enumName: "CanadaPostServices" }),
  }),
  z.object({
    carrier: createEnum([Carriers.PUROLATOR], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(PurolatorServices, { enumName: "PurolatorServices" }),
  }),
  z.object({
    carrier: createEnum([Carriers.GLS], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(GlsServices, { enumName: "GlsServices" }),
  }),
  z.object({
    carrier: createEnum([Carriers.ASENDIA], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(AsendiaServices, { enumName: "AsendiaServices" }),
  }),
  z.object({
    carrier: createEnum([Carriers.ROYAL_MAIL_V3], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(RoyalMailV3Services, {
      enumName: "RoyalMailV3Services",
    }),
  }),
  z.object({
    carrier: createEnum([Carriers.SPEE_DEE], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(SpeeDeeServices, { enumName: "SpeeDeeServices" }),
  }),
  z.object({
    carrier: createEnum([Carriers.PASSPORT_GLOBAL], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(PassportGlobalServices, {
      enumName: "PassportGlobalServices",
    }),
  }),
  z.object({
    carrier: createEnum([Carriers.EPOST_GLOBAL], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(EPostGlobalServices, {
      enumName: "EPostGlobalServices",
    }),
  }),
  z.object({
    carrier: createEnum([Carriers.LANDMARK_GLOBAL], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(LandmarkGlobalServices, {
      enumName: "LandmarkGlobalServices",
    }),
  }),
  z.object({
    carrier: createEnum([Carriers.ETA_EXPRESS], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(ETAExpressServices, { enumName: "ETAExpressServices" }),
  }),
  z.object({
    carrier: createEnum([Carriers.ONTRAC], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(OnTracServices, { enumName: "OnTracServices" }),
  }),
  z.object({
    carrier: createEnum([Carriers.GLOBALE], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(GlobalEServices, { enumName: "GlobalEServices" }),
  }),
  z.object({
    carrier: createEnum([Carriers.UPS_MAIL_INNOVATIONS], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(UpsMailInnovationsServices, {
      enumName: "UpsMailInnovationsServices",
    }),
  }),
  z.object({
    carrier: createEnum([Carriers.PUROLATOR], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(PurolatorServices, { enumName: "PurolatorServices" }),
  }),
  z.object({
    carrier: createEnum([Carriers.UNIUNI], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(UniUniServices, { enumName: "UniUniServices" }),
  }),
  z.object({
    carrier: createEnum([Carriers.STALLION_EXPRESS], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(StallionExpressServices, {
      enumName: "StallionExpressServices",
    }),
  }),
  z.object({
    carrier: createEnum([Carriers.CHITCHATS], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(ChitChatsServices, { enumName: "ChitChatsServices" }),
  }),
  z.object({
    carrier: createEnum([Carriers.POST_NL], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(PostNLServices, { enumName: "PostNLServices" }),
  }),
  z.object({
    carrier: createEnum([Carriers.INTELCOM], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(IntelcomServices, { enumName: "IntelcomServices" }),
  }),
  z.object({
    carrier: createEnum([Carriers.FLEET_OPTICS], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(FleetOpticsServices, {
      enumName: "FleetOpticsServices",
    }),
  }),
  z.object({
    carrier: createEnum([Carriers.ICS_COURIER], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(IcsCourierServices, { enumName: "IcsCourierServices" }),
  }),
  z.object({
    carrier: createEnum([Carriers.WHIZ_DELIVERY], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(WhizDeliveryServices, {
      enumName: "WhizDeliveryServices",
    }),
  }),
  z.object({
    carrier: createEnum([Carriers.TIKTOK_SHOP], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(TiktokShopServices, { enumName: "TiktokShopServices" }),
  }),
  z.object({
    carrier: createEnum([Carriers.CANPAR_EXPRESS], {
      enumName: "Carriers",
      isPartial: true,
      originalEnum: Carriers,
    }),
    service: createEnum(CanparServices, { enumName: "CanparServices" }),
  }),
]);
export const CorrelatedCarrierServiceSchema: z.ZodType<CorrelatedCarrierService> =
  CorrelatedCarrierServiceSchema_;
export type CorrelatedCarrierService = {} & z.infer<
  typeof CorrelatedCarrierServiceSchema_
>;

export function getAllServicesArray() {
  return [
    ...Object.values(FedexServices),
    ...Object.values(UpsServices),
    ...Object.values(UspsServices),
    ...Object.values(AmazonServices),
    ...Object.values(DhlExpressServices),
    ...Object.values(VehoServices),
    ...Object.values(ApcServices),
    ...Object.values(FirstMileServices),
    ...Object.values(AustraliaPostServices),
    ...Object.values(CanadaPostServices),
    ...Object.values(PurolatorServices),
    ...Object.values(DhlEcsServices),
    ...Object.values(GlsServices),
    ...Object.values(AsendiaServices),
    ...Object.values(RoyalMailV3Services),
    ...Object.values(SpeeDeeServices),
    ...Object.values(PassportGlobalServices),
    ...Object.values(EPostGlobalServices),
    ...Object.values(LandmarkGlobalServices),
    ...Object.values(ETAExpressServices),
    ...Object.values(OnTracServices),
    ...Object.values(GlobalEServices),
    ...Object.values(UpsMailInnovationsServices),
    ...Object.values(UniUniServices),
    ...Object.values(StallionExpressServices),
    ...Object.values(ChitChatsServices),
    ...Object.values(PostNLServices),
    ...Object.values(IntelcomServices),
    ...Object.values(FleetOpticsServices),
    ...Object.values(IcsCourierServices),
    ...Object.values(WhizDeliveryServices),
    ...Object.values(TiktokShopServices),
    ...Object.values(CanparServices),
  ];
}

export const Service = getAllServicesArray();

export const ServiceSchema: z.ZodType<Service> = ServiceSchema_;
